import { useCallback, useEffect, useState } from "react";
import { fetchFocusedSessionFee as apiFetchFocusedSessionFee } from "../api/conferenceRegistrationApi";

export const useFocusedSessions = ({
  conferenceApi,
  conferenceId,
  focusedSessions,
  focusedSessionAvailability,
  fetchFocusedSessions,
  additionalFees,
  zeroPayments,
  accompanyingPersonFee,
  setRegistrationJustUpdated,
}) => {
  const [registerForFocusedSessions, setRegisterForFocusedSessions] =
    useState("No");
  const [selectedFocusedSessions, setSelectedFocusedSessions] = useState([]);
  const [focusedSessionFees, setFocusedSessionFees] = useState({});

  const fetchFocusedSessionFee = useCallback(
    async (sessionId) => {
      if (!conferenceId || !sessionId) {
        return null;
      }

      try {
        return await apiFetchFocusedSessionFee({
          conferenceApi,
          conferenceId,
          sessionId,
        });
      } catch (error) {
        console.error(
          `Error fetching focused session fee for ${sessionId}:`,
          error
        );
        return null;
      }
    },
    [conferenceApi, conferenceId]
  );

  const handleFocusedSessionsChange = (value) => {
    setRegisterForFocusedSessions(value);
    if (value === "Yes" && focusedSessions.length === 0) {
      fetchFocusedSessions();
    }
    if (value === "No") {
      setSelectedFocusedSessions([]);
    }
  };

  const handleFocusedSessionCheckboxChange = async (sessionId) => {
    setRegistrationJustUpdated(false);
    setSelectedFocusedSessions((prev) => {
      const session = focusedSessions.find((focusedSession) => focusedSession.id === sessionId);
      const availability = focusedSessionAvailability[sessionId];
      const availableSeats = Number(
        availability?.availableSeats ??
          availability?.available_seats ??
          availability?.remainingSeats ??
          session?.availableSeats ??
          session?.available_seats ??
          session?.remainingSeats ??
          0
      );

      if (prev.includes(sessionId)) {
        setFocusedSessionFees((prevFees) => {
          const nextFees = { ...prevFees };
          delete nextFees[sessionId];
          return nextFees;
        });
        return prev.filter((id) => id !== sessionId);
      }

      if (availableSeats <= 0) {
        console.warn(
          `⚠️ Cannot select focused session ${sessionId}: no available seats`
        );
        return prev;
      }

      return [...prev, sessionId];
    });
  };

  const handleFocusedSessionRadioChange = (session) => {
    setRegistrationJustUpdated(false);
    setSelectedFocusedSessions([session.id]);
  };

  const loadFeesForSessions = useCallback(
    (sessionIds) => {
      sessionIds.forEach((sessionId) => {
        if (focusedSessionFees[sessionId]) {
          return;
        }

        fetchFocusedSessionFee(sessionId).then((feeData) => {
          if (feeData) {
            setFocusedSessionFees((prevFees) => ({
              ...prevFees,
              [sessionId]: feeData,
            }));
          }
        });
      });
    },
    [fetchFocusedSessionFee, focusedSessionFees]
  );

  const getFocusedSessionIdsFromFees = useCallback(
    ({ additionalFees: feeRows = [], zeroPayments: zeroPaymentRows = [] }) => {
      if (!accompanyingPersonFee) {
        return [];
      }

      const focusedSessionsFromAdditionalFees = feeRows
        .filter(
          (fee) =>
            fee.feeTypeId !== accompanyingPersonFee.id &&
            !fee.paymentId
        )
        .map((fee) => fee.feeTypeId);

      const focusedSessionsFromZeroPayments = zeroPaymentRows
        .filter((zeroPayment) => zeroPayment.feeTypeId !== accompanyingPersonFee.id)
        .map((zeroPayment) => zeroPayment.feeTypeId);

      return [
        ...new Set([
          ...focusedSessionsFromAdditionalFees,
          ...focusedSessionsFromZeroPayments,
        ]),
      ].filter(Boolean);
    },
    [accompanyingPersonFee]
  );

  const restoreFocusedSessionsFromFees = useCallback(
    ({ additionalFees: feeRows = [], zeroPayments: zeroPaymentRows = [] }) => {
      const focusedSessionsList = getFocusedSessionIdsFromFees({
        additionalFees: feeRows,
        zeroPayments: zeroPaymentRows,
      });

      if (focusedSessionsList.length === 0) {
        return;
      }

      setRegisterForFocusedSessions("Yes");
      setSelectedFocusedSessions(focusedSessionsList);
      loadFeesForSessions(focusedSessionsList);
    },
    [getFocusedSessionIdsFromFees, loadFeesForSessions]
  );

  useEffect(() => {
    if (conferenceId && selectedFocusedSessions.length > 0) {
      const sessionsNeedingFees = selectedFocusedSessions.filter(
        (sessionId) => !focusedSessionFees[sessionId]
      );

      sessionsNeedingFees.forEach((sessionId) => {
        fetchFocusedSessionFee(sessionId).then((feeData) => {
          if (feeData) {
            setFocusedSessionFees((prevFees) => ({
              ...prevFees,
              [sessionId]: feeData,
            }));
          }
        });
      });
    }
  }, [
    conferenceId,
    selectedFocusedSessions,
    focusedSessionFees,
    fetchFocusedSessionFee,
  ]);

  useEffect(() => {
    if (!accompanyingPersonFee || selectedFocusedSessions.length > 0) {
      return;
    }

    restoreFocusedSessionsFromFees({
      additionalFees: additionalFees || [],
      zeroPayments: zeroPayments || [],
    });
  }, [
    accompanyingPersonFee,
    additionalFees,
    zeroPayments,
    selectedFocusedSessions.length,
    restoreFocusedSessionsFromFees,
  ]);

  useEffect(() => {
    if (!conferenceId) {
      return;
    }

    const sessionIdsNeedingFees = [
      ...new Set(
        (additionalFees || [])
          .filter(
            (fee) =>
              !accompanyingPersonFee || fee.feeTypeId !== accompanyingPersonFee.id
          )
          .map((fee) => fee.feeTypeId)
          .filter((sessionId) => sessionId && !focusedSessionFees[sessionId])
      ),
    ];

    if (sessionIdsNeedingFees.length === 0) {
      console.log("✅ All additional fees have rates loaded");
      return;
    }

    console.log("📥 Fetching rates for sessions:", sessionIdsNeedingFees);

    sessionIdsNeedingFees.forEach((sessionId) => {
      fetchFocusedSessionFee(sessionId).then((feeData) => {
        console.log(`💰 Fetched rate for session ${sessionId}:`, feeData);
        if (feeData) {
          setFocusedSessionFees((prevFees) => ({
            ...prevFees,
            [sessionId]: feeData,
          }));
        } else {
          console.warn(`⚠️ No fee data returned for session ${sessionId}`);
        }
      });
    });
  }, [
    conferenceId,
    additionalFees,
    accompanyingPersonFee,
    focusedSessionFees,
    fetchFocusedSessionFee,
  ]);

  useEffect(() => {
    if (
      registerForFocusedSessions === "Yes" &&
      conferenceId &&
      focusedSessions.length === 0
    ) {
      fetchFocusedSessions();
    }
  }, [
    registerForFocusedSessions,
    conferenceId,
    focusedSessions.length,
    fetchFocusedSessions,
  ]);

  return {
    registerForFocusedSessions,
    setRegisterForFocusedSessions,
    selectedFocusedSessions,
    setSelectedFocusedSessions,
    focusedSessionFees,
    setFocusedSessionFees,
    fetchFocusedSessionFee,
    restoreFocusedSessionsFromFees,
    handleFocusedSessionsChange,
    handleFocusedSessionCheckboxChange,
    handleFocusedSessionRadioChange,
  };
};
